import { useApolloClient } from '@apollo/client';
import { useLanguage } from '@vocab/react';
import { useCallback, useEffect } from 'react';

import {
  SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY,
  SHOPPING_CART_CAJA_PRODUCT_SET_KEY,
} from 'src/app/shared/constants/shoppingCart';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import {
  GetAdProductsForAdPostingDocument,
  type GetAdProductsForAdPostingQuery,
  type AdProductSetResponse,
} from 'src/graphql/generated';

export type ProductList = Extract<
  GetAdProductsForAdPostingQuery['adProductsForAdPosting'],
  { __typename?: 'ProductList' }
> & { id?: string };

/**
 * This hook is likely to change, the contract to fetch product set is unstable
 */
export const useProductSetFromCart = ({
  draftId,
  subscriptionKey = SHOPPING_CART_CAJA_PRODUCT_SET_KEY,
}: {
  draftId?: string | null;
  subscriptionKey?: string;
}): ProductList | null => {
  const client = useApolloClient();
  const { language } = useLanguage();
  const {
    subscribeForProducts,
    unsubscribeForProducts,
    productResponses = {},
  } = useShoppingCart();

  const getAdProductsQuery = useCallback(
    async (productSetId: string) =>
      await client.query<AdProductSetResponse>({
        query: GetAdProductsForAdPostingDocument,
        variables: {
          input: { productSetId, locale: language },
          draftId,
        },
        fetchPolicy: 'cache-first',
      }),
    [client, draftId, language],
  );

  useEffect(() => {
    subscribeForProducts(
      subscriptionKey,
      SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY,
      getAdProductsQuery,
    );
    return () => {
      unsubscribeForProducts(subscriptionKey);
    };
  }, [
    getAdProductsQuery,
    subscribeForProducts,
    subscriptionKey,
    unsubscribeForProducts,
  ]);

  if (!productResponses[subscriptionKey]) return null;

  const { products } = productResponses[subscriptionKey];
  return Boolean(products) ? products : null;
};
